import { CheckCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { COURSES, MAX_PER_COURSE, groupCatalogue } from "@/lib/menuModule";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

// Seasonal menu picker shared by the client Menu page and the staff planning editor.
export function MenuSelection({ catalogue, form, setForm, locked = false }) {
  const grouped = groupCatalogue(catalogue);

  const toggle = (course, id) => {
    if (locked) {
      return;
    }
    const current = form[course] || [];
    if (current.includes(id)) {
      setForm({ ...form, [course]: current.filter((value) => value !== id) });
      return;
    }
    if (current.length >= MAX_PER_COURSE) {
      toast.error(`Up to ${MAX_PER_COURSE} dishes per course — deselect one first.`);
      return;
    }
    setForm({ ...form, [course]: [...current, id] });
  };

  return (
    <div className="space-y-10" data-testid="menu-selection">
      <div className="rounded-lg border border-border bg-card p-6">
        <Label className="eyebrow" htmlFor="menu-package">Wedding package</Label>
        <Input
          id="menu-package"
          className="mt-2"
          value={form.package || ""}
          disabled={locked}
          placeholder="e.g. Jacaranda"
          onChange={(event) => setForm({ ...form, package: event.target.value })}
          data-testid="field-package"
        />
        <p className="mt-3 text-xs text-muted-foreground">
          Canapés for canapé hour are Chef selection and take every dietary requirement into
          account. Our wedding menu is seasonal and changes twice a year.
        </p>
      </div>

      {COURSES.map((course) => {
        const items = grouped[course.key] || [];
        const selected = form[course.key] || [];
        const standard = items.filter((item) => !item.signature);
        const signature = items.filter((item) => item.signature);
        return (
          <section key={course.key} data-testid={`menu-course-${course.key}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="font-heading text-3xl text-primary">{course.label}</h2>
              <p
                className="text-xs uppercase tracking-wide text-secondary"
                data-testid={`menu-course-count-${course.key}`}
              >
                {selected.length} of {MAX_PER_COURSE} selected
              </p>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Choose up to {MAX_PER_COURSE} dishes — two choices are served alternate drop.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
              {standard.map((item) => (
                <DishCard
                  key={item.item_id}
                  item={item}
                  active={selected.includes(item.item_id)}
                  locked={locked}
                  onToggle={() => toggle(course.key, item.item_id)}
                />
              ))}
            </div>
            {signature.length > 0 && (
              <>
                <p className="eyebrow mt-8 flex items-center gap-2 text-accent">
                  <Sparkle size={14} weight="fill" /> Jacaranda signature
                </p>
                <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                  {signature.map((item) => (
                    <DishCard
                      key={item.item_id}
                      item={item}
                      active={selected.includes(item.item_id)}
                      locked={locked}
                      onToggle={() => toggle(course.key, item.item_id)}
                    />
                  ))}
                </div>
              </>
            )}
          </section>
        );
      })}

      <div className="rounded-lg border border-border bg-card p-6">
        <Label className="eyebrow" htmlFor="menu-notes">Notes for the Events Team</Label>
        <Textarea
          id="menu-notes"
          className="mt-2"
          rows={3}
          value={form.notes || ""}
          disabled={locked}
          placeholder="Anything we should know about your menu…"
          onChange={(event) => setForm({ ...form, notes: event.target.value })}
          data-testid="field-notes"
        />
      </div>
    </div>
  );
}

function DishCard({ item, active, locked, onToggle }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={locked && !active}
      aria-pressed={active}
      data-testid={`menu-item-${item.item_id}`}
      data-selected={active ? "true" : "false"}
      className={[
        "group relative flex flex-col rounded-lg border p-5 text-left transition-all duration-200",
        active
          ? "border-primary bg-primary/5 shadow-sm"
          : "border-border bg-card hover:-translate-y-0.5 hover:border-secondary hover:shadow-md",
        locked && !active ? "opacity-50" : "",
      ].join(" ")}
    >
      <span className="flex items-start justify-between gap-3">
        <span className="font-heading text-xl text-primary">{item.name}</span>
        {active && (
          <CheckCircle size={20} weight="fill" className="shrink-0 text-primary" />
        )}
      </span>
      <span className="mt-2 text-sm text-muted-foreground">{item.description}</span>
      {item.dietary && (
        <span className="mt-3 text-[11px] uppercase tracking-[0.15em] text-secondary">
          {item.dietary}
        </span>
      )}
    </button>
  );
}
